<template>
    <div class="afs">
        <div class="afs__segments" role="radiogroup" :aria-label="label">
            <button v-for="opt in scopeOptions" :key="opt.type" type="button" role="radio" class="afs__segment"
                :class="{ 'is-active': scopeType === opt.type }" :aria-checked="scopeType === opt.type"
                @click="setScope(opt.type)">
                {{ opt.label }}
            </button>
        </div>
        <p class="afs__hint">{{ activeHint }}</p>

        <div v-if="scopeType === 4" class="afs__picker">
            <template v-if="isListMode">
                <label class="afs__search">
                    <svg class="afs__search-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M10.5 3a7.5 7.5 0 0 1 5.96 12.06l4.24 4.24a1 1 0 0 1-1.42 1.42l-4.24-4.24A7.5 7.5 0 1 1 10.5 3Zm0 2a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z"/></svg>
                    <input v-model.trim="query" type="search" class="afs__search-input" placeholder="Filter affiliates" />
                </label>
                <div class="afs__results" role="listbox">
                    <button v-for="a in listResults" :key="a.id" type="button" class="afs__result" role="option"
                        :class="{ 'is-checked': isSelected(a.id) }" :aria-selected="isSelected(a.id)" @click="toggleListItem(a)">
                        <span class="afs__result-check" :class="{ 'is-checked': isSelected(a.id) }"><check-icon /></span>
                        <span class="afs__result-name">{{ a.name }}</span>
                    </button>
                    <div v-if="listResults.length === 0 && query" class="afs__empty">No affiliates match "{{ query }}"</div>
                    <div v-else-if="listResults.length === 0" class="afs__empty">No affiliates appear in the current results.</div>
                </div>
            </template>

            <template v-else>
                <label class="afs__search">
                    <svg class="afs__search-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M10.5 3a7.5 7.5 0 0 1 5.96 12.06l4.24 4.24a1 1 0 0 1-1.42 1.42l-4.24-4.24A7.5 7.5 0 1 1 10.5 3Zm0 2a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z"/></svg>
                    <input v-model="query" type="search" class="afs__search-input"
                        placeholder="Search affiliates by name or email" @input="onQueryInput" />
                    <span v-if="isLoading" class="afs__spinner" aria-label="Searching"></span>
                </label>
                <div v-if="showResults" class="afs__results" role="listbox">
                    <button v-for="a in visibleResults" :key="a.id" type="button" class="afs__result" role="option" @click="add(a)">
                        <span class="afs__result-plus">+</span>
                        <span class="afs__result-name">{{ a.name }}</span>
                    </button>
                    <div v-if="results.length > maxResults" class="afs__note">Showing the first {{ maxResults }} of {{ results.length.toLocaleString() }} matches — keep typing to narrow it down.</div>
                    <div v-if="results.length === 0 && !isLoading" class="afs__empty">No affiliates found for "{{ query }}"</div>
                </div>
                <p v-else-if="query.trim().length > 0 && query.trim().length <= minChars" class="afs__note">Keep typing — at least {{ minChars + 1 }} characters.</p>
            </template>

            <div class="afs__chips">
                <span v-if="selected.length === 0" class="afs__none">No affiliates selected yet — sales from any affiliate will show until you add one.</span>
                <button v-for="a in selected" :key="a.id" type="button" class="afs__chip" @click="remove(a.id)">
                    <span class="afs__chip-avatar">{{ initials(a.name) }}</span>
                    <span class="afs__chip-name">{{ a.name }}</span>
                    <cross-icon class="afs__chip-x" />
                </button>
            </div>
        </div>
    </div>
</template>
<script>
import axios from 'axios';
import CheckIcon from '@/assets/images/icons/CheckIcon.svg';
import CrossIcon from '@/assets/images/icons/CrossIcon.svg';

// Affiliate scope control: All / Only affiliates / Only non-affiliates /
// Specific affiliates, with an inline picker for the last option.
//
// v-model carries the same array the page state uses:
//   [{ id: 0, name: 'All' }]                    -> all
//   [{ id: -2, name: 'Only Affiliates' }]       -> only affiliates
//   [{ id: -1, name: 'Only Non-Affiliates' }]   -> only non-affiliates
//   [] or [{ id, name }, ...]                   -> specific affiliates
//
// Without `options` the picker searches the API (funnel-affiliates/search);
// with `options` it filters that list locally (used by the failed-rebills tab).
export default {
    name: 'AffiliateScope',
    components: { CheckIcon, CrossIcon },
    props: {
        value: { type: Array, default: () => [] },
        label: { type: String, default: 'Affiliates' },
        options: { type: Array, default: null },
        funnel: { default: 0 },
    },
    data() {
        return {
            query: '',
            results: [],
            isLoading: false,
            searched: false,
            timer: null,
            minChars: 2,
            maxResults: 50,
        };
    },
    computed: {
        scopeType() {
            const v = this.value || [];
            if (v.length === 0) return 4;
            if (v[0].id == 0) return 1;
            if (v[0].id == -1) return 3;
            if (v[0].id == -2) return 2;
            return 4;
        },
        scopeOptions() {
            return [
                { type: 1, label: this.$t('frontend_analytics_all'), hint: this.$t('frontend_analytics_all_transactions') },
                { type: 2, label: this.$t('frontend_analytics_only_affiliates'), hint: this.$t('frontend_analytics_only_the_sales') },
                { type: 3, label: this.$t('frontend_analytics_only_non_affiliates'), hint: this.$t('frontend_analytics_only_sales_without') },
                { type: 4, label: this.$t('frontend_groovesell_customers_specific_aff'), hint: this.$t('frontend_analytics_only_sales_made_by') },
            ];
        },
        activeHint() {
            return this.scopeOptions.find((o) => o.type === this.scopeType)?.hint || '';
        },
        selected() {
            return this.scopeType === 4 ? (this.value || []) : [];
        },
        selectedIds() {
            return this.selected.map((a) => a.id);
        },
        isListMode() {
            return Array.isArray(this.options);
        },
        listResults() {
            const q = this.query.toLowerCase();
            return (this.options || [])
                .filter((a) => a.id != 0 && a.id != -1 && a.id != -2)
                .filter((a) => !q || String(a.name || '').toLowerCase().includes(q));
        },
        showResults() {
            return this.searched && this.query.trim().length > this.minChars;
        },
        visibleResults() {
            return this.results.slice(0, this.maxResults);
        },
    },
    methods: {
        emit(list) {
            this.$emit('input', list);
        },
        setScope(type) {
            if (type === this.scopeType) return;
            if (type === 1) this.emit([{ id: 0, name: 'All' }]);
            else if (type === 2) this.emit([{ id: -2, name: 'Only Affiliates' }]);
            else if (type === 3) this.emit([{ id: -1, name: 'Only Non-Affiliates' }]);
            else this.emit([]);
            this.query = '';
            this.results = [];
            this.searched = false;
        },
        isSelected(id) {
            return this.selectedIds.indexOf(id) !== -1;
        },
        add(affiliate) {
            if (this.isSelected(affiliate.id)) return;
            this.emit([...this.selected, affiliate]);
            this.query = '';
            this.results = [];
            this.searched = false;
        },
        remove(id) {
            this.emit(this.selected.filter((a) => a.id !== id));
        },
        toggleListItem(a) {
            if (this.isSelected(a.id)) this.remove(a.id);
            else this.emit([...this.selected, a]);
        },
        onQueryInput() {
            clearTimeout(this.timer);
            const q = this.query.trim();
            if (q.length <= this.minChars) {
                this.results = [];
                this.searched = false;
                return;
            }
            this.timer = setTimeout(() => this.search(q), 400);
        },
        async search(q) {
            this.isLoading = true;
            try {
                const { data } = await axios.post('funnel-affiliates/search', {
                    funnel_id: this.funnel,
                    query: q,
                    excluded: this.selectedIds,
                });
                if (this.query.trim() === q) {
                    this.results = Array.isArray(data) ? data : [];
                    this.searched = true;
                }
            } catch (e) {
                this.results = [];
                this.searched = true;
            }
            this.isLoading = false;
        },
        initials(name) {
            return String(name || '')
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w[0].toUpperCase())
                .join('');
        },
    },
};
</script>
<style lang="scss">
.afs {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &__segments {
        display: inline-grid;
        grid-template-columns: repeat(4, auto);
        gap: 4px;
        padding: 4px;
        border-radius: 12px;
        background: var(--af-surface-2);
        align-self: flex-start;
        max-width: 100%;
    }

    &__segment {
        padding: 8px 14px;
        border: 0;
        border-radius: 9px;
        background: transparent;
        font-size: 13.5px;
        font-weight: 600;
        color: var(--af-muted);
        white-space: nowrap;
        cursor: pointer;
        transition: background 120ms ease, color 120ms ease, box-shadow 120ms ease;

        &:hover { color: var(--af-fg); }
        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--af-primary-ring); }

        &.is-active {
            background: #fff;
            color: var(--af-fg);
            box-shadow: 0 1px 2px rgba(24, 34, 48, 0.08), 0 0 0 1px rgba(24, 34, 48, 0.04);
        }
    }

    &__hint {
        margin: 0;
        font-size: 13px;
        color: var(--af-muted);
    }

    &__picker {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 6px;
        padding: 14px;
        border: 1px solid var(--af-border);
        border-radius: 12px;
        background: var(--af-surface);
    }

    &__search {
        position: relative;
        display: flex;
        align-items: center;
        height: 40px;
        padding: 0 12px 0 38px;
        border: 1px solid var(--af-border);
        border-radius: var(--af-radius);
        background: #fff;
        transition: border-color 120ms ease, box-shadow 120ms ease;

        &:focus-within { border-color: var(--af-primary); box-shadow: 0 0 0 3px var(--af-primary-ring); }
    }

    &__search-icon { position: absolute; left: 13px; width: 15px; height: 15px; fill: var(--af-faint); }

    &__search-input {
        flex: 1 1 auto;
        min-width: 0;
        border: 0;
        outline: none;
        background: transparent;
        font: inherit;
        font-size: 14px;
        color: var(--af-fg);

        &::placeholder { color: var(--af-faint); }
        &::-webkit-search-cancel-button { display: none; }
    }

    &__spinner {
        width: 14px;
        height: 14px;
        border-radius: 999px;
        border: 2px solid var(--af-border);
        border-top-color: var(--af-primary);
        animation: afs-spin 700ms linear infinite;
    }

    &__results {
        max-height: 200px;
        overflow-y: auto;
        border: 1px solid var(--af-border);
        border-radius: var(--af-radius);
        background: #fff;
        padding: 4px;
    }

    &__result {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 8px 10px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        text-align: left;
        font-size: 13.5px;
        color: var(--af-fg);
        cursor: pointer;

        &:hover { background: var(--af-surface); }
        &:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--af-primary-ring); }
        &.is-checked .afs__result-name { font-weight: 600; }
    }

    &__result-plus {
        width: 18px;
        height: 18px;
        border-radius: 6px;
        background: var(--af-primary-soft);
        color: var(--af-primary);
        font-weight: 800;
        font-size: 14px;
        line-height: 18px;
        text-align: center;
    }

    &__result-check {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 5px;
        border: 1.5px solid var(--af-border-strong);
        background: #fff;
        color: #fff;

        svg { width: 11px; height: 11px; fill: currentColor; }
        &.is-checked { background: var(--af-primary); border-color: var(--af-primary); }
    }

    &__result-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

    &__empty, &__note {
        margin: 0;
        padding: 10px 4px;
        font-size: 13px;
        color: var(--af-faint);
    }

    &__chips { display: flex; flex-wrap: wrap; gap: 8px; }

    &__none { font-size: 13px; color: var(--af-muted); }

    &__chip {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 4px 8px 4px 4px;
        border-radius: 999px;
        border: 1px solid var(--af-border);
        background: #fff;
        font-size: 13px;
        color: var(--af-fg);
        cursor: pointer;
        transition: border-color 100ms ease;

        &:hover { border-color: var(--af-primary); .afs__chip-x { color: var(--af-primary); } }
        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--af-primary-ring); }
    }

    &__chip-avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 999px;
        background: var(--af-primary-soft);
        color: var(--af-primary);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.02em;
    }

    &__chip-name { font-weight: 600; }
    &__chip-x { width: 12px; height: 12px; fill: currentColor; color: var(--af-faint); }

    @media (max-width: 760px) {
        &__segments { grid-template-columns: repeat(2, 1fr); align-self: stretch; }
        &__segment { padding: 8px 10px; white-space: normal; }
    }
}

@keyframes afs-spin { to { transform: rotate(360deg); } }
</style>
